import React from 'react';

const ROM_JOINTS = {
  'Upper Limb': [
    { id: 'shoulder',     label: 'Shoulder',       movements: ['Flexion','Extension','Abduction','Adduction','IR','ER','Horizontal Add'] },
    { id: 'elbow',        label: 'Elbow',           movements: ['Flexion','Extension'] },
    { id: 'forearm',      label: 'Forearm',         movements: ['Pronation','Supination'] },
    { id: 'wrist',        label: 'Wrist',           movements: ['Flexion','Extension','Radial Dev.','Ulnar Dev.'] },
    { id: 'hand_fingers', label: 'Hands & Fingers', movements: ['Grip','Pinch','Finger Flex','Finger Ext','Opposition'] },
  ],
  'Lower Limb': [
    { id: 'hip',   label: 'Hip',   movements: ['Flexion','Extension','Abduction','Adduction','IR','ER'] },
    { id: 'knee',  label: 'Knee',  movements: ['Flexion','Extension'] },
    { id: 'ankle', label: 'Ankle', movements: ['Dorsiflexion','Plantarflexion','Inversion','Eversion'] },
    { id: 'foot',  label: 'Foot',  movements: ['Toe Flexion','Toe Extension'] },
  ],
  'Spine': [
    { id: 'cervical',  label: 'Cervical Spine',  movements: ['Flexion','Extension','LF (R)','LF (L)','Rot (R)','Rot (L)'] },
    { id: 'thoracic',  label: 'Thoracic Spine',  movements: ['Flexion','Extension','LF (R)','LF (L)','Rot (R)','Rot (L)'] },
    { id: 'lumbar',    label: 'Lumbar Spine',    movements: ['Flexion','Extension','LF (R)','LF (L)','Rot (R)','Rot (L)'] },
  ],
};

const SECTION_COLORS = {
  'Upper Limb': { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  'Lower Limb': { color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
  'Spine':      { color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
};

/* ── tiny angle input ── */
const AngleInput = ({ label, value, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
    <span style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', lineHeight: 1 }}>{label}</span>
    <div style={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <input
        type="number" min="0" max="360" placeholder="—"
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        style={{
          width: 46, padding: '5px 4px', borderRadius: 7,
          border: '1.5px solid #e2e8f0', fontSize: '0.85rem', fontWeight: 700,
          textAlign: 'center', outline: 'none', background: '#f8fafc',
          color: '#0f172a', boxSizing: 'border-box',
        }}
      />
      <span style={{ fontSize: 9, color: '#94a3b8' }}>°</span>
    </div>
  </div>
);

/* ── active / passive pair ── */
const AngleGroup = ({ label, rtKey, ltKey, value, onChange }) => {
  const rt = value[rtKey] ?? '';
  const lt = value[ltKey] ?? '';
  const asym = rt !== '' && lt !== '' && Math.abs(parseFloat(rt) - parseFloat(lt)) > 10;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4 }}>
      <span style={{ fontSize: 9, fontWeight: 700, lineHeight: 1, marginBottom: 2, whiteSpace: 'nowrap', color: asym ? '#ea580c' : '#94a3b8' }}>
        {label}{asym ? ' ⚠' : ''}
      </span>
      <AngleInput label="RT" value={rt} onChange={v => onChange({ [rtKey]: v })} />
      <AngleInput label="LT" value={lt} onChange={v => onChange({ [ltKey]: v })} />
    </div>
  );
};

/* ── pain toggle ── */
const PainToggle = ({ value, onChange }) => {
  const on = value === 'yes';
  return (
    <button type="button" onClick={() => onChange(on ? '' : 'yes')} style={{
      padding: '4px 10px', borderRadius: 99, cursor: 'pointer', fontWeight: 700, fontSize: 10,
      border: `1.5px solid ${on ? '#ef4444' : '#e2e8f0'}`,
      background: on ? '#fef2f2' : '#f8fafc',
      color: on ? '#ef4444' : '#94a3b8',
      flexShrink: 0, transition: 'all 0.15s', whiteSpace: 'nowrap',
    }}>
      {on ? '⚡ Painful' : 'Pain-free'}
    </button>
  );
};

/* ── end feel dropdown ── */
const EndFeelSelect = ({ value, onChange }) => (
  <select value={value || ''} onChange={e => onChange(e.target.value)} style={{
    padding: '5px 7px', borderRadius: 7, border: '1.5px solid #e2e8f0',
    fontSize: 11, fontWeight: 600, color: value ? '#374151' : '#94a3b8',
    background: '#f8fafc', outline: 'none', cursor: 'pointer',
    flex: '0 1 108px', minWidth: 88,
  }}>
    <option value="">End feel…</option>
    <option>Normal</option><option>Firm</option><option>Hard</option>
    <option>Empty</option><option>Springy</option><option>Boggy</option>
    <option>Not Tested</option>
  </select>
);

/* ══════════════════════════════════════════════════════
   JOINT ROW
══════════════════════════════════════════════════════ */
const JointRow = ({ joint, sc, value, update }) => {
  const get    = k => value[k] ?? '';
  const status = get(`${joint.id}_status`);   // '' | 'intact' | 'rom'
  const isIntact = status === 'intact';
  const isROM    = status === 'rom';
  const note = get(`${joint.id}_note`);

  const setStatus = (s) => update({ [`${joint.id}_status`]: status === s ? '' : s });

  return (
    <div style={{ borderBottom: '0.5px solid #f1f5f9' }}>

      {/* ─── header row ─── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', flexWrap: 'wrap',
        background: isIntact ? '#f0fdf4' : isROM ? '#f5f8ff' : '#fff',
        transition: 'background 0.15s',
      }}>

        {/* Joint label */}
        <div style={{ flex: '0 0 118px', minWidth: 100 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{joint.label}</div>
          {isIntact && <div style={{ fontSize: 9, color: '#059669', fontWeight: 700 }}>✓ Intact</div>}
          {isROM    && <div style={{ fontSize: 9, color: '#2563eb', fontWeight: 700 }}>📐 ROM recorded</div>}
        </div>

        {/* Choice buttons */}
        <button
          type="button"
          onClick={() => setStatus('intact')}
          style={{
            padding: '7px 18px', borderRadius: 99, fontWeight: 700, fontSize: 12, cursor: 'pointer',
            border: `1.5px solid ${isIntact ? '#10b981' : '#e2e8f0'}`,
            background: isIntact ? '#ecfdf5' : '#f8fafc',
            color: isIntact ? '#059669' : '#94a3b8',
            transition: 'all 0.15s', minHeight: 34,
          }}
        >
          {isIntact ? '✓ Intact' : 'Intact'}
        </button>

        <button
          type="button"
          onClick={() => setStatus('rom')}
          style={{
            padding: '7px 18px', borderRadius: 99, fontWeight: 700, fontSize: 12, cursor: 'pointer',
            border: `1.5px solid ${isROM ? '#3b82f6' : '#e2e8f0'}`,
            background: isROM ? '#eff6ff' : '#f8fafc',
            color: isROM ? '#2563eb' : '#94a3b8',
            transition: 'all 0.15s', minHeight: 34,
          }}
        >
          {isROM ? 'ROM ▾' : 'ROM'}
        </button>

        {/* note */}
        <input
          type="text" placeholder="Note…" value={note}
          onChange={e => update({ [`${joint.id}_note`]: e.target.value })}
          style={{
            flex: '1 1 100px', minWidth: 80, padding: '5px 8px', borderRadius: 7,
            border: '1px solid #e2e8f0', fontSize: 11, color: '#374151',
            outline: 'none', background: note ? '#fafbff' : '#fff', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* ─── ROM expanded: per-movement angle rows ─── */}
      {isROM && (
        <div style={{ background: '#f8fafc', borderTop: '0.5px solid #dbeafe' }}>

          {/* column headers */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 14px 3px 28px', flexWrap: 'wrap' }}>
            <div style={{ flex: '0 0 110px', fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Movement</div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8' }}>Active  RT / LT</div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', marginLeft: 12 }}>Passive  RT / LT</div>
          </div>

          {joint.movements.map(mv => {
            const mk     = `${joint.id}_mv_${mv.replace(/\s+/g, '_').toLowerCase()}`;
            const mvPain = get(`${mk}_pain`);
            const mvEF   = get(`${mk}_ef`);
            const mvNote = get(`${mk}_note`);

            return (
              <div key={mv} className="rom-mv-row" style={{
                padding: '7px 14px 7px 28px', borderTop: '0.5px solid #f1f5f9',
                background: mvPain === 'yes' ? '#fff8f8' : 'transparent',
              }}>
                <div style={{ fontSize: 11, color: '#475569', fontStyle: 'italic', flex: '0 0 110px', minWidth: 90 }}>
                  {mv}
                </div>

                <div className="rom-angle-stack">
                  <AngleGroup
                    label="Active"
                    rtKey={`${mk}_active_rt`} ltKey={`${mk}_active_lt`}
                    value={value} onChange={update}
                  />
                  <AngleGroup
                    label="Passive"
                    rtKey={`${mk}_passive_rt`} ltKey={`${mk}_passive_lt`}
                    value={value} onChange={update}
                  />
                </div>

                <PainToggle value={mvPain} onChange={v => update({ [`${mk}_pain`]: v })} />
                <EndFeelSelect value={mvEF}  onChange={v => update({ [`${mk}_ef`]: v })} />

                <input
                  type="text" placeholder="Note…" value={mvNote}
                  onChange={e => update({ [`${mk}_note`]: e.target.value })}
                  style={{
                    flex: '1 1 90px', minWidth: 70, padding: '4px 7px', borderRadius: 7,
                    border: '1px solid #e2e8f0', fontSize: 11, color: '#374151',
                    outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════ */
const ROMAssessment = ({ value = {}, onChange }) => {
  const [open, setOpen] = React.useState(false);

  const get    = k  => value[k] ?? '';
  const update = partial => onChange?.({ ...value, ...partial });

  const allJoints     = Object.values(ROM_JOINTS).flat();
  const intactCount   = allJoints.filter(j => get(`${j.id}_status`) === 'intact').length;
  const romCount      = allJoints.filter(j => get(`${j.id}_status`) === 'rom').length;
  const assessedCount = intactCount + romCount;

  /* closed-state button */
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} style={{
        width: '100%', padding: '15px', borderRadius: 14,
        border: '1px solid #e2e8f0', backgroundColor: 'transparent', color: '#3730a3',
        fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer',
        display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10,
        transition: 'all 0.2s', marginBottom: 8,
      }}>
        + Range of Motion Assessment
        {intactCount > 0 && (
          <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 20, background: '#059669', color: '#fff', fontWeight: 800 }}>
            {intactCount} intact
          </span>
        )}
        {romCount > 0 && (
          <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 20, background: '#2563eb', color: '#fff', fontWeight: 800 }}>
            {romCount} ROM recorded
          </span>
        )}
      </button>
    );
  }

  /* open panel */
  return (
    <div style={{ padding: 20, marginBottom: 8 }}>
      <style>{`.rom-mv-row{display:flex;align-items:center;gap:8px;flex-wrap:wrap}.rom-angle-stack{display:flex;align-items:center;gap:8px;flex-wrap:wrap}@media(max-width:480px){.rom-mv-row{flex-direction:column;align-items:flex-start;padding-left:14px!important}.rom-angle-stack{gap:12px}}`}</style>

      {/* header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontWeight: 800, fontSize: '1rem', color: '#3730a3' }}>📐 Range of Motion Assessment</span>
        <button type="button" onClick={() => setOpen(false)}
          style={{ background: 'none', border: 'none', color: '#cbd5e1', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1 }}
          onMouseEnter={e => e.target.style.color = '#ef4444'}
          onMouseLeave={e => e.target.style.color = '#cbd5e1'}
        >×</button>
      </div>

      {/* summary bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: '10px 16px',
        borderRadius: 12, marginBottom: 14, flexWrap: 'wrap',
        background: assessedCount > 0 ? '#f0fdf4' : '#f8fafc',
        border: `1px solid ${assessedCount > 0 ? '#6ee7b7' : '#e2e8f0'}`,
      }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: assessedCount > 0 ? '#059669' : '#94a3b8' }}>
          {assessedCount}
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: assessedCount > 0 ? '#065f46' : '#64748b' }}>
            {assessedCount === 0
              ? 'No joints assessed yet'
              : `${assessedCount} of 14 joints assessed — ${intactCount} intact, ${romCount} with ROM angles`}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
            Select <strong>Intact</strong> for joints with full ROM, or <strong>ROM</strong> to enter angles per movement
          </div>
        </div>
      </div>

      {/* joint sections */}
      {Object.entries(ROM_JOINTS).map(([section, joints]) => {
        const sc = SECTION_COLORS[section];
        return (
          <div key={section} style={{ marginBottom: 12, border: `0.5px solid ${sc.border}`, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ background: sc.bg, padding: '6px 14px', borderBottom: `0.5px solid ${sc.border}` }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: sc.color, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                {section}
              </span>
            </div>
            <div style={{ background: '#fff' }}>
              {joints.map(j => (
                <JointRow key={j.id} joint={j} sc={sc} value={value} update={update} />
              ))}
            </div>
          </div>
        );
      })}

      {assessedCount > 0 && (
        <button type="button" onClick={() => onChange({})}
          style={{ marginTop: 6, fontSize: 12, background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', textDecoration: 'underline' }}>
          Clear all ROM data
        </button>
      )}
    </div>
  );
};

export default ROMAssessment;
